import { useState } from 'react';
import { useStories, useStoryDecision } from '../../api/projects.js';
import type { Story } from '../../api/types.js';
import { Alert, Button, Card, Spinner } from '../../components/ui.js';
import { StoryComments } from '../../components/Discussion.js';
import { useFocusStory } from '../../components/useFocusStory.js';
import { StoryStatusBadge } from '../../status.js';
import { useProjectContext } from './ProjectShell.js';

/**
 * Founder-facing, read-only view of the user stories: grouped by epic, each with
 * its current status so the founder can see what the developers are building.
 */
export function StoriesPage() {
  const { project } = useProjectContext();
  const stories = useStories(project.id);
  const [showDone, setShowDone] = useState(false);
  // A message can deep-link here with ?story=<id>; scroll to and highlight it.
  const focusStoryId = useFocusStory(!stories.isPending);

  if (stories.isPending) return <Card>{<Spinner />}</Card>;
  if (stories.isError) return <Card>{<Alert>Could not load your user stories.</Alert>}</Card>;

  const ordered = stories.data;

  if (ordered.length === 0) {
    return (
      <Card>
        <h2 className="mb-2 font-title text-2xl tracking-wide">User stories</h2>
        <p className="opacity-70">
          Once your specification is approved, our team splits it into small user stories that get
          built one at a time. They’ll appear here — grouped by epic, each showing its status.
        </p>
      </Card>
    );
  }

  // Tested stories drop to a separate "Done" section so the active work is clear.
  const active = ordered.filter((s) => s.status !== 'accepted');
  const done = ordered.filter((s) => s.status === 'accepted');
  // Open the Done section if the story we're deep-linking to lives there.
  const doneOpen = showDone || (focusStoryId ? done.some((s) => s.id === focusStoryId) : false);

  return (
    <Card>
      <h2 className="mb-3 font-title text-2xl tracking-wide">User stories</h2>
      {active.length > 0 ? (
        <ol className="flex flex-col gap-3">
          {active.map((story, i) => {
            const showEpic = i === 0 || active[i - 1]?.epic !== story.epic;
            return (
              <div
                key={story.id}
                id={`story-${story.id}`}
                className="flex flex-col gap-2 rounded-md transition"
              >
                {showEpic ? (
                  <h3 className="mt-2 text-sm font-semibold uppercase tracking-wide text-primary">
                    {story.epic || 'Ungrouped'}
                  </h3>
                ) : null}
                <FounderStoryItem projectId={project.id} story={story} />
              </div>
            );
          })}
        </ol>
      ) : (
        <p className="opacity-70">Every story has been built and tested. 🎉</p>
      )}

      {done.length > 0 ? (
        <div className="mt-6 border-t border-secondary/10 pt-4">
          <button
            className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-secondary/70 hover:text-secondary"
            onClick={() => setShowDone((v) => !v)}
          >
            <span className="text-xs">{doneOpen ? '▾' : '▸'}</span>
            Done — {done.length} tested {done.length === 1 ? 'story' : 'stories'}
          </button>
          {doneOpen ? (
            <ol className="mt-3 flex flex-col gap-3">
              {done.map((story) => (
                <div
                  key={story.id}
                  id={`story-${story.id}`}
                  className="flex flex-col gap-2 rounded-md transition"
                >
                  <FounderStoryItem projectId={project.id} story={story} />
                </div>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** Formats equivalent developer hours compactly, e.g. `6h` or `6.5h`. */
function hoursLabel(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

function FounderStoryItem({ projectId, story }: { projectId: string; story: Story }) {
  const [showComments, setShowComments] = useState(false);
  const decide = useStoryDecision(projectId);
  // `ready` means a developer has readied this story for the founder's sign-off.
  const awaitingApproval = story.status === 'ready';
  return (
    <li className="rounded-md bg-neutral/50 p-4">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm opacity-60">#{story.sequence}</span>
        <span className="font-medium">{story.title}</span>
        <span className="ml-auto">
          <StoryStatusBadge status={story.status} />
        </span>
      </div>
      {story.equivalentDevHours !== null ? (
        <p className="mb-2 text-sm font-medium text-primary">
          ≈ {hoursLabel(story.equivalentDevHours)} of developer time
        </p>
      ) : null}
      <p className="text-sm leading-relaxed">
        As <em>{story.asA}</em>, I want {story.iWant}, so that {story.soThat}.
      </p>
      {story.acceptanceCriteria.length > 0 ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm opacity-80">
          {story.acceptanceCriteria.map((c, idx) => (
            <li key={idx}>{c}</li>
          ))}
        </ul>
      ) : null}
      {awaitingApproval ? (
        <div className="mt-3 flex flex-col gap-2 border-t border-secondary/10 pt-3">
          <p className="text-sm font-medium">This story is ready for your approval.</p>
          {decide.isError ? <Alert>Could not record your approval. Please try again.</Alert> : null}
          <div>
            <Button
              disabled={decide.isPending}
              onClick={() => decide.mutate({ storyId: story.id, decision: 'approve' })}
            >
              {decide.isPending ? 'Approving…' : 'Approve story'}
            </Button>
          </div>
        </div>
      ) : null}
      <button
        className="mt-3 text-sm text-primary underline"
        onClick={() => setShowComments((v) => !v)}
      >
        {showComments ? 'Hide comments' : 'Comments'}
      </button>
      {showComments ? (
        <div className="mt-3 border-t border-secondary/10 pt-3">
          <StoryComments projectId={projectId} storyId={story.id} canMarkInternal={false} />
        </div>
      ) : null}
    </li>
  );
}
